import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import ComparePicker from "../components/ComparePicker";
import { isRender } from "../catalog";
import { useLang } from "../lang";
import { CATEGORIES } from "../catalog";
import { useCompare } from "../components/CompareContext";
import { compareTable, productsBySlugs, familyOf, FAMILY_LABELS, MAX_COMPARE } from "../compare";
import Breadcrumb from "../components/Breadcrumb";
import DatasheetLink, { DATASHEETS_ENABLED } from "../components/DatasheetLink";
import DatasheetAccess from "../components/DatasheetAccess";
import { useAuth } from "../auth";

function EmptyState({ lang, count, onAdd }) {
  return (
    <div className="text-center py-20 px-6" data-testid="compare-empty">
      <p className="text-[17px] text-[#1d1d1f]/80 max-w-[520px] mx-auto">
        {lang === "it"
          ? "Seleziona almeno due sistemi della stessa famiglia con il pulsante «Confronta» sulle schede prodotto."
          : "Select at least two systems of the same family with the «Compare» button on product cards."}
      </p>
      {count === 1 ? (
        <button type="button" onClick={onAdd} className="btn-pill btn-primary mt-8 inline-flex" data-testid="compare-empty-add">
          {lang === "it" ? "Aggiungi un sistema" : "Add a system"} <Plus size={15} className="ml-1" />
        </button>
      ) : (
        <Link to="/prodotti/architettonici" className="btn-pill btn-primary mt-8 inline-flex" data-testid="compare-browse">
          {lang === "it" ? "Sfoglia i sistemi" : "Browse systems"}
        </Link>
      )}
    </div>
  );
}

function HeaderCell({ p, lang, onRemove }) {
  return (
    <th className="align-top p-3 md:p-5 font-normal text-left" data-testid={`compare-col-${p.slug}`}>
      <div className="relative bg-white rounded-2xl border border-[#e4e4e7] h-[170px] flex items-center justify-center p-4">
        <img src={p.image} alt={p[lang].name} className="w-full h-full object-contain" />
        <button type="button" onClick={() => onRemove(p.slug)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#f5f5f7] hover:bg-[#e4e4e7] flex items-center justify-center" aria-label="Rimuovi" data-testid={`compare-remove-${p.slug}`}>
          <X size={14} />
        </button>
      </div>
      <Link to={`/prodotti/p/${p.slug}`} className="block mt-4 text-[18px] md:text-[20px] font-semibold text-[#1d1d1f] hover:text-[#1D3F94]">{p[lang].name}</Link>
      <p className="mt-1 text-[13px] text-[#6e6e73] leading-snug">{p[lang].tagline}</p>
      <div className="mt-3 text-[12px] uppercase tracking-widest text-[#6e6e73]">{CATEGORIES[p.category]?.[lang].title}</div>
    </th>
  );
}

function MobileCompare({ items, rows, lang, onRemove, onAdd, showSheets }) {
  const cols = { gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` };
  return (
    <div className="md:hidden" data-testid="compare-mobile">
      <div className="sticky top-[56px] z-20 -mx-6 px-4 py-3 bg-[#f5f5f7]/95 backdrop-blur-xl border-b border-[#e4e4e7]">
        <div className="grid gap-2" style={cols}>
          {items.map((p) => (
            <div key={p.slug} className="relative bg-white rounded-xl border border-[#e4e4e7] p-2 text-center" data-testid={`compare-m-col-${p.slug}`}>
              <img src={p.image} alt="" className={`w-full h-9 ${isRender(p.image) ? "object-contain" : "object-cover rounded-lg"}`} />
              <Link to={`/prodotti/p/${p.slug}`} className="mt-1.5 block text-[13px] font-semibold leading-tight text-[#1d1d1f] break-words">{p[lang].name}</Link>
              <button type="button" onClick={() => onRemove(p.slug)} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#1d1d1f] text-white flex items-center justify-center" aria-label="Rimuovi" data-testid={`compare-m-remove-${p.slug}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        {items.length < MAX_COMPARE && (
          <button type="button" onClick={onAdd} className="mt-2.5 w-full text-[13px] font-medium text-[#1D3F94] inline-flex items-center justify-center gap-1.5 py-1.5" data-testid="compare-m-add">
            <Plus size={14} /> {lang === "it" ? "Aggiungi un sistema" : "Add a system"}
          </button>
        )}
      </div>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.key} className="bg-white rounded-2xl border border-[#e4e4e7] p-4" data-testid={`compare-m-row-${r.key}`}>
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6e6e73] font-medium mb-2.5">{r.label}</div>
            {r.cells.some((c) => c && c.text.length > 22) ? (
              <div className="divide-y divide-[#f0f0f2]">
                {r.cells.map((c, i) => (
                  <div key={i} className="py-2 first:pt-0 last:pb-0 flex gap-3 text-[13.5px] leading-snug">
                    <span className="w-[72px] shrink-0 font-semibold text-[#1d1d1f] truncate">{items[i][lang].name}</span>
                    <span className={r.bestIdx.includes(i) ? "font-semibold text-[#1D3F94]" : "text-[#1d1d1f]"}>{c ? c.text : <span className="text-[#a1a1a6]">—</span>}</span>
                  </div>
                ))}
              </div>
            ) : (
            <div className="grid gap-3" style={cols}>
              {r.cells.map((c, i) => {
                const best = r.bestIdx.includes(i);
                return (
                  <div key={i} lang={lang} className={`text-[13.5px] leading-snug [overflow-wrap:break-word] ${i > 0 ? "border-l border-[#e4e4e7] pl-3" : ""} ${best ? "font-semibold text-[#1D3F94]" : "text-[#1d1d1f]"}`}>
                    {c ? c.text : <span className="text-[#a1a1a6]">—</span>}
                    {best && <span className="mt-1 block w-fit text-[9.5px] uppercase tracking-wider bg-[#1D3F94] text-white rounded-full px-1.5 py-0.5">{lang === "it" ? "Migliore" : "Best"}</span>}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        ))}
        {showSheets && (
          <div className="bg-white rounded-2xl border border-[#e4e4e7] p-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6e6e73] font-medium mb-2.5">{lang === "it" ? "Scheda tecnica" : "Datasheet"}</div>
            <div className="grid gap-3" style={cols}>
              {items.map((p) => (
                <div key={p.slug} className="text-[13.5px]">
                  {p.brochure ? <a href={p.brochure} target="_blank" rel="noopener noreferrer" className="a-link">PDF <span className="chev">↓</span></a> : <span className="text-[#a1a1a6]">—</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DesktopStickyBar({ items, lang, show }) {
  return (
    <div className={`hidden md:block fixed top-14 inset-x-0 z-30 bg-[#f5f5f7]/90 backdrop-blur-xl border-b border-[#e4e4e7] transition-[opacity,transform] duration-300 ${show ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3 pointer-events-none"}`} data-testid="compare-sticky-bar">
      <div className="max-w-[1200px] mx-auto px-6 grid items-center py-2.5" style={{ gridTemplateColumns: `200px repeat(${items.length}, minmax(0, 1fr))` }}>
        <span className="text-[11px] uppercase tracking-wider text-[#6e6e73]">{lang === "it" ? "Confronto" : "Comparison"}</span>
        {items.map((p) => (
          <Link key={p.slug} to={`/prodotti/p/${p.slug}`} className="flex items-center gap-3 px-5 min-w-0 group">
            <img src={p.image} alt="" className="w-10 h-10 object-contain bg-white rounded-lg border border-[#e4e4e7] p-0.5 shrink-0" />
            <span className="text-[15px] font-semibold text-[#1d1d1f] truncate group-hover:text-[#1D3F94]">{p[lang].name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Compare() {
  const { lang } = useLang();
  const { slugs, remove: drop, toggle } = useCompare();
  const remove = (slug) => {
    const name = productsBySlugs([slug])[0]?.[lang].name;
    drop(slug);
    toast(lang === "it" ? `${name} rimosso dal confronto` : `${name} removed from comparison`, {
      action: { label: lang === "it" ? "Annulla" : "Undo", onClick: () => toggle(slug, lang) },
    });
  };
  const { user } = useAuth();
  const items = productsBySlugs(slugs);
  const rows = compareTable(items, lang);
  const fam = items[0] ? familyOf(items[0]) : null;
  const [picker, setPicker] = useState(false);
  const headRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (!headRef.current) return undefined;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting && e.boundingClientRect.top < 0), { rootMargin: "-140px 0px 0px 0px" });
    io.observe(headRef.current);
    return () => io.disconnect();
  }, [items.length]);
  const showSheets = DATASHEETS_ENABLED || !!user;

  return (
    <main className="min-h-screen bg-[#f5f5f7] pt-20 pb-32 md:pb-24" data-testid="compare-page">
      <div className="max-w-[1200px] mx-auto px-6 pt-8">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: lang === "it" ? "Catalogo" : "Catalogue", to: "/prodotti" }, { label: lang === "it" ? "Confronta sistemi" : "Compare systems" }]} />
        <h1 className="mt-8 text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-[#1d1d1f]">{lang === "it" ? "Confronta sistemi" : "Compare systems"}</h1>
        {fam && <p className="mt-3 text-base md:text-lg text-[#6e6e73]" data-testid="compare-family">{FAMILY_LABELS[fam][lang]}</p>}
      </div>

      {items.length < 2 ? <EmptyState lang={lang} count={items.length} onAdd={() => setPicker(true)} /> : (
        <div className="max-w-[1200px] mx-auto px-6 mt-6 md:mt-10">
          <MobileCompare items={items} rows={rows} lang={lang} onRemove={remove} onAdd={() => setPicker(true)} showSheets={showSheets} />
          {items.length < MAX_COMPARE && (
            <div className="hidden md:flex justify-end mb-4">
              <button type="button" onClick={() => setPicker(true)} className="a-link inline-flex items-center gap-1.5 text-[14px]" data-testid="compare-add">
                <Plus size={15} /> {lang === "it" ? "Aggiungi un sistema" : "Add a system"}
              </button>
            </div>
          )}
          <DesktopStickyBar items={items} lang={lang} show={stuck} />
          <div className="hidden md:block" data-testid="compare-table-wrapper">
            <table className="w-full table-fixed border-collapse" data-testid="compare-table">
              <thead ref={headRef}>
                <tr>
                  <th className="w-[200px]" />
                  {items.map((p) => <HeaderCell key={p.slug} p={p} lang={lang} onRemove={remove} />)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-[#e4e4e7] hover:bg-white/70 transition-colors" data-testid={`compare-row-${r.key}`}>
                    <th scope="row" className="text-left align-top py-4 pr-4 text-[12px] uppercase tracking-wider text-[#6e6e73] font-medium leading-snug">{r.label}</th>
                    {r.cells.map((c, i) => {
                      const best = r.bestIdx.includes(i);
                      return (
                        <td key={i} className={`align-top py-4 px-5 text-[15px] text-[#1d1d1f] transition-colors ${best ? "bg-[#1D3F94]/[0.045]" : ""}`} data-testid={`compare-cell-${r.key}-${i}`}>
                          {c ? (
                            <span className={best ? "font-semibold text-[#1D3F94]" : ""} data-best={best || undefined}>
                              {c.text}
                              {best && <span className="mt-1.5 block w-fit text-[10px] uppercase tracking-wider bg-[#1D3F94] text-white rounded-full px-2 py-0.5">{lang === "it" ? "Migliore" : "Best"}</span>}
                            </span>
                          ) : <span className="text-[#a1a1a6]">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {showSheets && <tr className="border-t border-[#e4e4e7]">
                  <th scope="row" className="text-left py-4 pr-4 text-[12px] uppercase tracking-wider text-[#6e6e73] font-medium">{lang === "it" ? "Scheda tecnica" : "Datasheet"}</th>
                  {items.map((p) => (
                    <td key={p.slug} className="p-4 md:px-5">
                      {p.brochure ? (
                        <DatasheetLink url={p.brochure} slug={p.slug} name={p[lang].name} className="a-link text-[14px]" testId={`compare-brochure-${p.slug}`}>
                          {lang === "it" ? "Scarica PDF" : "Download PDF"} <span className="chev">↓</span>
                        </DatasheetLink>
                      ) : <span className="text-[#a1a1a6]">—</span>}
                    </td>
                  ))}
                </tr>}
              </tbody>
            </table>
          </div>
          {!DATASHEETS_ENABLED && !user && items.some((p) => p.brochure) && (
            <div className="mt-8" data-testid="compare-datasheet-access">
              <DatasheetAccess />
            </div>
          )}
          <p className="mt-4 text-[12px] text-[#6e6e73] max-w-[760px]">
            {lang === "it"
              ? "Valori di sistema dichiarati da ASAŞ: le prestazioni del serramento finito dipendono da vetro, ferramenta, dimensioni e configurazione."
              : "System values declared by ASAŞ: finished window performance depends on glazing, hardware, size and configuration."}
          </p>
        </div>
      )}
      <ComparePicker open={picker} onClose={() => setPicker(false)} />
    </main>
  );
}
